// src/views/Settings/org/OrganizationMembers.jsx
//
// Miembros y roles de una organización. Salió de `OrganizationsView.jsx`, donde
// vivía dentro de un modal a pantalla completa: la administración de una
// organización no es un diálogo, es una sección con su propia URL.
//
// Cuelga de la organización y no de la cuenta porque `memberships` cuelga de
// `organizations` en la base de datos — quién pertenece a qué es del contenedor,
// no de la cuenta que hay dentro.
import React from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import Spinner from "../../../components/Spinner";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { XCircle } from "lucide-react";
import OrganizationService from "../../../services/organization.service";
import { messageForResult } from "../../../services/accountDomainErrorMessages";
import { PRIMARY, BUTTON_SIZE } from '../../../lib/buttonStyles';

export default function OrganizationMembers({ organizationId, organizationName, currentUserRole, onClose }) {
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('MEMBER');
  const [inviting, setInviting] = React.useState(false);
  const [message, setMessage] = React.useState(null);
  const [memberToRemove, setMemberToRemove] = React.useState(null);

  const myRole = currentUserRole?.toLowerCase();
  const isOwner = myRole === 'owner';
  const canManage = isOwner || myRole === 'admin';

  // El backend deja que un owner gestione a otro owner y frena solo cuando la
  // organización se quedaría sin ninguno (`LAST_OWNER_PROTECTED`). El front
  // ocultaba los controles en toda fila de owner, así que esa regla no se
  // podía alcanzar y transferir la propiedad era imposible desde acá.
  // Un admin sigue sin poder tocarlos: eso sí lo prohíbe el backend.
  const canManageMember = (member) =>
    canManage && (member.role?.toLowerCase() !== 'owner' || isOwner);

  // Cargar miembros
  React.useEffect(() => {
    loadMembers();
  }, [organizationId]);

  const loadMembers = async () => {
    setLoading(true);
    const result = await OrganizationService.listMembers(organizationId);
    if (result.success) {
      setMembers(result.data);
    } else {
      setMessage({ type: 'error', text: messageForResult(result, 'Failed to load members') });
    }
    setLoading(false);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    setMessage(null);

    const result = await OrganizationService.addMember(organizationId, {
      email: inviteEmail,
      role: inviteRole
    });

    if (result.success) {
      setMessage({ type: 'success', text: 'Member added successfully!' });
      setInviteEmail('');
      await loadMembers();
    } else {
      // Antes se buscaba 'not found' dentro del mensaje. El backend dejó de
      // filtrar el texto de la excepción y la rama quedó muerta sin que nada
      // lo notara: el `code` es lo que no cambia al reescribir el copy.
      setMessage({ type: 'error', text: messageForResult(result, 'Failed to add member') });
    }
    setInviting(false);
  };

  const handleRemoveMember = async (userId) => {
    setMemberToRemove(userId);
  };

  const confirmRemoveMember = async () => {
    if (!memberToRemove) return;

    const result = await OrganizationService.removeMember(organizationId, memberToRemove);
    if (result.success) {
      setMessage({ type: 'success', text: 'Member removed successfully!' });
      await loadMembers();
    } else {
      setMessage({ type: 'error', text: messageForResult(result, 'Failed to remove member') });
    }
    setMemberToRemove(null);
  };

  const cancelRemoveMember = () => {
    setMemberToRemove(null);
  };

  const handleChangeRole = async (userId, newRole) => {
    const result = await OrganizationService.updateMemberRole(organizationId, userId, newRole);
    if (result.success) {
      setMessage({ type: 'success', text: 'Role updated successfully!' });
      await loadMembers();
    } else {
      setMessage({ type: 'error', text: messageForResult(result, 'Failed to update role') });
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      OWNER: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
      ADMIN: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
      MEMBER: 'text-gray-400 bg-gray-500/20 border-gray-500/30',
      VIEWER: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30'
    };
    return colors[role] || colors.MEMBER;
  };

  return (
    <div className="space-y-6">
      {/* Invite Form */}
      {canManage && (
        <form onSubmit={handleInvite} className="space-y-4 p-4 bg-black/20 rounded-lg border border-white/10">
          <h3 className="text-lg font-semibold text-white">Add Member</h3>
          <div className="flex gap-3">
            <Input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1 bg-black/40 border-white/20 text-white"
              required
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="bg-black/40 border border-white/20 text-white rounded-md px-3 py-2"
            >
              <option value="VIEWER">Viewer</option>
              <option value="MEMBER">Member</option>
              <option value="ADMIN">Admin</option>
            </select>
            <Button
              type="submit"
              disabled={inviting}
              className={PRIMARY}
            >
              {inviting ? 'Adding...' : 'Add Member'}
            </Button>
          </div>
        </form>
      )}

      {/* Messages */}
      {message && (
        <div className={`p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
            : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}>
          {message.text}
        </div>
      )}

      {/* Members List */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Members ({members.length})
        </h3>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" color="purple" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No members yet
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.user_id}
                className="flex items-center justify-between p-4 bg-black/20 rounded-lg border border-white/10 hover:border-white/20 transition"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    {member.display_name?.charAt(0).toUpperCase() || member.username?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {member.display_name || member.username || member.email}
                    </div>
                    <div className="text-sm text-gray-400">
                      {member.email}
                    </div>
                    {member.joined_at && (
                      <div className="text-xs text-gray-500">
                        Joined {new Date(member.joined_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {canManageMember(member) ? (
                    <select
                      value={member.role?.toUpperCase()}
                      onChange={(e) => handleChangeRole(member.user_id, e.target.value)}
                      className="bg-black/40 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm"
                    >
                      <option value="VIEWER">Viewer</option>
                      <option value="MEMBER">Member</option>
                      <option value="ADMIN">Admin</option>
                      {/* Promover a owner es lo que pide el mensaje de
                          `LAST_OWNER_PROTECTED`; sin esta opción el usuario no
                          tenía cómo hacerle caso. Solo un owner puede. */}
                      {isOwner && <option value="OWNER">Owner</option>}
                    </select>
                  ) : (
                    <Badge className={getRoleColor(member.role?.toUpperCase())}>
                      {member.role?.toUpperCase()}
                    </Badge>
                  )}
                  
                  {canManageMember(member) && (
                    <Button
                      onClick={() => handleRemoveMember(member.user_id)}
                      variant="outline"
                      size="sm"
                      className="bg-black/40 border-white/20 text-red-400 hover:bg-red-500/20 hover:border-red-500"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Remove Member Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!memberToRemove}
        title="Remove Member"
        message="Are you sure you want to remove this member? They will lose access to this organization."
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmRemoveMember}
        onCancel={cancelRemoveMember}
      />
    </div>
  );
}
